import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import { useReactToPrint } from "react-to-print";
import {
  Search,
  ChevronDown,
  ChevronRight,
  FileText,
  AlertCircle,
  Package,
  Printer,
  MapPin,
  Calendar,
  User,
  CheckCircle,
  Info,
  Hash,
  Plus,
  Edit,
  Ban,
} from "lucide-react";

import Loader from "../../loader/Loader";
import Pagination from "../Pagination";
import { domain } from "../../../security";
import { selectLocationId } from "../../../redux/IchthusSlice";

// Print Component Placeholder (Ensure you have this or rename to your actual print component)
import { QuotationSalesPrint } from "./QuotationSalesPrint";
import AddQuotationSales from "./AddQuotationSales";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(amount || 0);

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const calculateOriginalTotal = (qs) => {
  if (!qs?.quotationSalesProducts) return 0;
  return qs.quotationSalesProducts.reduce(
    (acc, item) => acc + (item.quantity || 0) * (item.unitPrice || 0),
    0,
  );
};

const StatusBadge = ({ status }) => {
  const s = status?.toLowerCase() || "";
  let color = "bg-gray-50 text-gray-600 border-gray-200";
  let icon = <Info size={11} />;

  if (s === "active") {
    color = "bg-blue-50 text-blue-700 border-blue-200";
    icon = <FileText size={11} />;
  } else if (s === "converted") {
    color = "bg-emerald-50 text-emerald-700 border-emerald-200";
    icon = <CheckCircle size={11} />;
  } else if (s === "cancelled") {
    color = "bg-red-50 text-red-700 border-red-200";
    icon = <Ban size={11} />;
  }

  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${color}`}
    >
      {icon}
      <span className="capitalize">{status}</span>
    </div>
  );
};

const PriceDisplay = ({ currentTotal, originalTotal }) => {
  const hasChanged = Math.abs(currentTotal - originalTotal) > 0.1;
  return (
    <div className="flex flex-col items-end">
      <span className="font-bold text-gray-900">
        {formatCurrency(currentTotal)}
      </span>
      {hasChanged && (
        <span className="text-xs text-gray-400 line-through">
          {formatCurrency(originalTotal)}
        </span>
      )}
    </div>
  );
};

const AllQuotationSales = () => {
  const userLocationId = useSelector(selectLocationId);
  const [qsData, setQsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [expandedQS, setExpandedQS] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [activeTab, setActiveTab] = useState("active");
  const [allLocationsList, setAllLocationsList] = useState([]);

  // MODAL STATES
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedQSToEdit, setSelectedQSToEdit] = useState(null);

  const [printData, setPrintData] = useState(null);
  const printQSRef = useRef();

  const locations = useMemo(() => {
    if (allLocationsList.length === 0) return ["All"];
    const names = [
      ...new Set(allLocationsList.map((l) => l.locationName)),
    ].sort();
    return [...names, "All"];
  }, [allLocationsList]);

  useEffect(() => {
    if (allLocationsList.length > 0 && selectedLocation === "") {
      if (userLocationId) {
        const userLoc = allLocationsList.find(
          (l) => l.id === parseInt(userLocationId),
        );
        setSelectedLocation(userLoc ? userLoc.locationName : "All");
      } else {
        setSelectedLocation("All");
      }
    }
  }, [allLocationsList, userLocationId, selectedLocation]);

  const handlePrintQS = useReactToPrint({
    content: () => printQSRef.current,
    documentTitle: `QS_${printData?.quoteNumber || "Document"}`,
  });

  const triggerPrint = (qs) => {
    setPrintData(qs);
    setTimeout(() => handlePrintQS(), 300);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [qsRes, locRes] = await Promise.all([
        axios.get(`${domain}/api/QuotationSales`),
        axios.get(`${domain}/api/Locations`),
      ]);
      setAllLocationsList(locRes.data);
      setQsData(qsRes.data.sort((a, b) => b.id - a.id));
    } catch {
      toast.error("Failed to fetch Quotation Sales.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditQS = (id) => {
    const qsToEdit = qsData.find((qs) => qs.id === id);
    if (qsToEdit) {
      if (qsToEdit.status === "Converted") {
        toast.error("Cannot edit a converted quotation.");
        return;
      }
      setSelectedQSToEdit(qsToEdit);
      setIsEditModalOpen(true);
    }
  };

  const handleCancelQS = async (id) => {
    const target = qsData.find((q) => q.id === id);
    if (target?.status === "Converted") {
      toast.error("Cannot cancel a converted quotation.");
      return;
    }

    if (!window.confirm("Are you sure you want to cancel this Quotation?"))
      return;

    try {
      await axios.put(`${domain}/api/QuotationSales/cancel/${id}`);
      toast.success("Quotation Sales cancelled.");
      if (expandedQS === id) setExpandedQS(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to cancel quotation.");
    }
  };

  const tabFilteredQS = qsData.filter((qs) => {
    const s = qs.status?.toLowerCase() || "";
    if (activeTab === "active") return s === "active";
    if (activeTab === "converted") return s === "converted";
    if (activeTab === "cancelled") return s === "cancelled";
    return false;
  });

  const filteredQS = tabFilteredQS.filter((qs) => {
    const matchesSearch =
      (qs.quoteNumber || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (qs.customerName || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLocation =
      selectedLocation === "All" ||
      qs.locationName === selectedLocation ||
      selectedLocation === "";
    return matchesSearch && matchesLocation;
  });

  const currentQS = filteredQS.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const activeCount = qsData.filter(
    (s) => s.status?.toLowerCase() === "active",
  ).length;
  const convertedCount = qsData.filter(
    (s) => s.status?.toLowerCase() === "converted",
  ).length;
  const cancelledCount = qsData.filter(
    (s) => s.status?.toLowerCase() === "cancelled",
  ).length;

  const toggleExpandQS = (id) => setExpandedQS(expandedQS === id ? null : id);

  return (
    <div className="min-h-screen pb-12 bg-gray-50">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER SECTION */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-100 rounded-2xl shadow-sm">
                <FileText size={22} className="text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 leading-tight">
                  Quotation Sales
                </h1>
                <p className="text-gray-500 text-sm">
                  Create and manage quotes before converting them to Sales
                  Orders
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition shadow-md hover:shadow-lg"
              >
                <Plus size={16} />
                Create Quotation
              </button>
            </div>
          </div>

          <div className="flex gap-1 mt-5 p-1 bg-gray-100 rounded-xl max-w-max">
            {[
              {
                key: "active",
                label: "Active",
                count: activeCount,
                icon: <FileText size={13} />,
              },
              {
                key: "converted",
                label: "Converted",
                count: convertedCount,
                icon: <CheckCircle size={13} />,
              },
              {
                key: "cancelled",
                label: "Cancelled",
                count: cancelledCount,
                icon: <Ban size={13} />,
              },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                  setCurrentPage(1);
                }}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  activeTab === tab.key
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {tab.icon}
                {tab.label}
                <span
                  className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                    activeTab === tab.key
                      ? "bg-blue-50 text-blue-600"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-5">
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-200 mb-5">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Quote number or customer name..."
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all text-sm outline-none"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="relative min-w-[180px]">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                value={selectedLocation}
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-9 pr-8 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-400 appearance-none bg-white text-sm outline-none"
              >
                {locations.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc === "All" ? "All Locations" : loc}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-24">
            <Loader />
          </div>
        ) : (
          <>
            <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full text-sm text-left text-gray-600">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="w-10 px-5 py-3.5"></th>
                    <th className="px-5 py-3.5 font-semibold">Quote #</th>
                    <th className="px-5 py-3.5 font-semibold">Customer</th>
                    <th className="px-5 py-3.5 font-semibold">Salesman</th>
                    <th className="px-5 py-3.5 font-semibold">Quote Date</th>
                    <th className="px-5 py-3.5 font-semibold">Status</th>
                    <th className="px-5 py-3.5 font-semibold text-right">
                      Amount
                    </th>
                    <th className="px-5 py-3.5 font-semibold text-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {currentQS.map((qs) => (
                    <React.Fragment key={qs.id}>
                      <tr
                        onClick={() => toggleExpandQS(qs.id)}
                        className={`hover:bg-blue-50/30 transition cursor-pointer ${
                          expandedQS === qs.id ? "bg-blue-50/20" : ""
                        }`}
                      >
                        <td className="px-5 py-3.5 text-center">
                          <button className="p-1 hover:bg-gray-200 rounded-lg transition text-gray-400">
                            {expandedQS === qs.id ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </button>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="font-bold text-gray-900">
                            {qs.quoteNumber}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="font-medium text-gray-900">
                            {qs.customerName}
                          </p>
                          <p className="text-xs text-gray-400">
                            {qs.locationName}
                          </p>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="font-medium text-gray-900">
                            {qs.salesmanName}
                          </p>
                        </td>
                        <td className="px-5 py-3.5 text-gray-500">
                          {formatDate(qs.date)}
                        </td>
                        <td className="px-5 py-3.5">
                          <StatusBadge status={qs.status} />
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <PriceDisplay
                            currentTotal={qs.totalAmount}
                            originalTotal={calculateOriginalTotal(qs)}
                          />
                        </td>
                        <td className="px-5 py-3.5">
                          <div
                            className="flex items-center justify-center gap-1.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => triggerPrint(qs)}
                              title="Print Quotation"
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
                            >
                              <Printer size={16} />
                            </button>
                            {qs.status === "Active" && (
                              <>
                                <button
                                  onClick={() => handleEditQS(qs.id)}
                                  title="Edit Quotation"
                                  className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition"
                                >
                                  <Edit size={16} />
                                </button>
                                <button
                                  onClick={() => handleCancelQS(qs.id)}
                                  title="Cancel Quotation"
                                  className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                >
                                  <Ban size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>

                      {expandedQS === qs.id && (
                        <tr>
                          <td colSpan="7" className="bg-slate-50/70 px-6 py-5">
                            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-6">
                                {[
                                  {
                                    icon: <MapPin size={11} />,
                                    label: "Location",
                                    value: qs.locationName || "N/A",
                                  },
                                  {
                                    icon: <Calendar size={11} />,
                                    label: "Validity Date",
                                    value: formatDate(qs.validityDate),
                                  },
                                  {
                                    icon: <User size={11} />,
                                    label: "Customer Code",
                                    value: qs.customerCode || "—",
                                  },
                                  {
                                    icon: <FileText size={11} />,
                                    label: "VAT Configuration",
                                    value: qs.hasVat ? "VAT Applied" : "No VAT",
                                  },
                                ].map((info) => (
                                  <div key={info.label} className="space-y-1">
                                    <span className="text-xs text-gray-400 uppercase font-semibold flex items-center gap-1">
                                      {info.icon} {info.label}
                                    </span>
                                    <p className="text-sm font-medium text-gray-800">
                                      {info.value}
                                    </p>
                                  </div>
                                ))}
                                {qs.remarks && (
                                  <div className="col-span-2 md:col-span-4 bg-amber-50 p-3 rounded-lg border border-amber-100">
                                    <span className="text-xs text-amber-700 font-bold uppercase block mb-1">
                                      Notes
                                    </span>
                                    <p className="text-sm text-amber-900">
                                      {qs.remarks}
                                    </p>
                                  </div>
                                )}
                              </div>

                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-bold text-gray-800 text-xs uppercase tracking-wide flex items-center gap-1.5">
                                  <Package size={14} /> Quotation Items
                                </h4>
                              </div>

                              <div className="border border-gray-200 rounded-xl overflow-hidden mb-5">
                                <table className="w-full text-xs text-left text-gray-600">
                                  <thead className="bg-gray-50 text-gray-500 uppercase font-semibold border-b border-gray-100">
                                    <tr>
                                      <th className="px-4 py-2.5">Item Code</th>
                                      <th className="px-4 py-2.5">Product</th>
                                      <th className="px-4 py-2.5 text-center">
                                        Qty
                                      </th>
                                      <th className="px-4 py-2.5 text-center">
                                        UOM
                                      </th>
                                      <th className="px-4 py-2.5 text-right">
                                        Unit Price
                                      </th>
                                      <th className="px-4 py-2.5 text-right">
                                        Line Total
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {(qs.quotationSalesProducts || []).map(
                                      (item) => (
                                        <tr
                                          key={item.id}
                                          className="hover:bg-gray-50 transition"
                                        >
                                          <td className="px-4 py-2.5">
                                            <span className="inline-flex items-center gap-1 text-xs font-mono text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
                                              <Hash size={10} />
                                              {item.itemCode || "—"}
                                            </span>
                                          </td>
                                          <td className="px-4 py-2.5 font-medium text-gray-900">
                                            {item.productName}
                                          </td>
                                          <td className="px-4 py-2.5 text-center text-gray-700 font-bold">
                                            {item.quantity}
                                          </td>
                                          <td className="px-4 py-2.5 text-center text-gray-500">
                                            {item.uomName || "—"}
                                          </td>
                                          <td className="px-4 py-2.5 text-right">
                                            {formatCurrency(item.unitPrice)}
                                          </td>
                                          <td className="px-4 py-2.5 text-right font-bold text-gray-900">
                                            {formatCurrency(item.lineTotal)}
                                          </td>
                                        </tr>
                                      ),
                                    )}
                                  </tbody>
                                </table>
                              </div>

                              <div className="flex justify-end">
                                <div className="w-full md:w-72 bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-2 text-sm">
                                  {qs.vatAmount > 0 && (
                                    <div className="flex justify-between text-gray-500">
                                      <span>VAT (12%)</span>
                                      <span className="font-medium text-gray-700">
                                        {formatCurrency(qs.vatAmount)}
                                      </span>
                                    </div>
                                  )}
                                  {qs.ewtAmount > 0 && (
                                    <div className="flex justify-between text-gray-500">
                                      <span>EWT (1%)</span>
                                      <span className="font-medium text-red-500">
                                        -{formatCurrency(qs.ewtAmount)}
                                      </span>
                                    </div>
                                  )}
                                  <div className="border-t border-gray-200 pt-3 flex justify-between font-bold text-gray-900">
                                    <span>Grand Total</span>
                                    <PriceDisplay
                                      currentTotal={qs.totalAmount}
                                      originalTotal={calculateOriginalTotal(qs)}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}

                  {currentQS.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center">
                          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <AlertCircle size={22} className="text-gray-400" />
                          </div>
                          <p className="text-gray-700 font-semibold">
                            No quotations found
                          </p>
                          <p className="text-gray-400 text-sm mt-1">
                            {searchTerm
                              ? "Try a different search term."
                              : "No records to display for this category."}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* MOBILE VIEW */}
            <div className="lg:hidden space-y-3">
              {currentQS.map((qs) => (
                <div
                  key={qs.id}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div
                    className="p-4 cursor-pointer"
                    onClick={() => toggleExpandQS(qs.id)}
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className="p-1.5 bg-blue-50 rounded-lg shrink-0">
                          <FileText size={15} className="text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-gray-900 text-sm">
                            {qs.quoteNumber}
                          </h3>
                          <p className="text-xs text-gray-500 truncate">
                            {qs.customerName}
                          </p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <StatusBadge status={qs.status} />
                        <div className="mt-1.5">
                          <PriceDisplay
                            currentTotal={qs.totalAmount}
                            originalTotal={calculateOriginalTotal(qs)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {expandedQS === qs.id && (
                    <div className="border-t border-gray-100 bg-gray-50 p-4">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => triggerPrint(qs)}
                          className="flex-1 py-2 bg-white text-gray-600 rounded-lg text-xs font-medium border border-gray-200 flex items-center justify-center gap-1.5"
                        >
                          <Printer size={14} /> Print
                        </button>

                        {qs.status === "Active" && (
                          <>
                            <button
                              onClick={() => handleEditQS(qs.id)}
                              className="flex-1 py-2 px-3 bg-blue-50 text-blue-600 rounded-lg text-xs font-medium border border-blue-100 flex items-center justify-center gap-1.5"
                            >
                              <Edit size={14} /> Edit
                            </button>
                            <button
                              onClick={() => handleCancelQS(qs.id)}
                              className="flex-1 py-2 px-3 bg-red-50 text-red-600 rounded-lg text-xs font-medium border border-red-100 flex items-center justify-center gap-1.5"
                            >
                              <Ban size={14} /> Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {currentQS.length === 0 && (
                <div className="text-center py-14 text-gray-400 bg-white rounded-2xl border border-gray-100">
                  <AlertCircle size={36} className="mx-auto mb-2 opacity-40" />
                  <p className="font-medium text-gray-500">No quotes found.</p>
                </div>
              )}
            </div>

            <div className="mt-5">
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={filteredQS.length}
                currentPage={currentPage}
                paginate={(p) => setCurrentPage(p)}
              />
            </div>
          </>
        )}
      </div>

      <div style={{ display: "none" }}>
        <QuotationSalesPrint ref={printQSRef} data={printData} />
      </div>

      {isAddModalOpen && (
        <AddQuotationSales
          onClose={() => setIsAddModalOpen(false)}
          refreshData={fetchData}
        />
      )}

      {isEditModalOpen && selectedQSToEdit && (
        <AddQuotationSales
          qsData={selectedQSToEdit}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedQSToEdit(null);
          }}
          refreshData={fetchData}
        />
      )}
    </div>
  );
};

export default AllQuotationSales;
